import { useNavigate, useParams } from "@tanstack/react-router";
import { useAuth0 } from "@auth0/auth0-react";
import { Building2, RefreshCw, Users } from "lucide-react";
import { useAcceptInvite, useInvitePreview } from "../api/hooks";
import type { ApiError } from "../api/client";
import { useActiveSpace } from "../api/space-context";

// Tela de aceite de convite (/invite/:token). O usuário já está logado (o Gate
// garante). Mostra o preview e aceita; ao aceitar, torna o espaço o ativo.

export function AcceptInviteRoute() {
  const { token } = useParams({ from: "/invite/$token" });
  const { user } = useAuth0();
  const preview = useInvitePreview(token);
  const accept = useAcceptInvite();
  const { setActiveSpace } = useActiveSpace();
  const navigate = useNavigate();

  const onAccept = () => {
    accept.mutate(token, {
      onSuccess: (res) => {
        setActiveSpace({ type: res.scope, id: res.scopeId });
        navigate({ to: "/" });
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-50 px-4 dark:bg-slate-950">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        {preview.isLoading && <p className="text-sm text-slate-500 dark:text-slate-400">Carregando convite…</p>}

        {/* Sem este ramo o card ficava VAZIO quando a chamada falhava (403 de
            auth, 404 de token inválido) — o convite parecia "não carregar". */}
        {preview.isError && (
          <div>
            <h1 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Não foi possível abrir o convite
            </h1>
            <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
              {(preview.error as ApiError).status === 404
                ? "Este convite expirou, já foi usado ou não existe. Peça um novo para quem te convidou."
                : (preview.error as Error).message}
            </p>
            <button
              type="button"
              onClick={() => preview.refetch()}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Tentar de novo
            </button>
          </div>
        )}

        {preview.data && !preview.data.valid && (
          <div>
            <h1 className="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100">Convite inválido</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Este convite expirou, já foi usado ou não existe. Peça um novo para quem te convidou.
            </p>
          </div>
        )}

        {preview.data?.valid && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary-light">
                {preview.data.scope === "org" ? (
                  <Building2 className="h-5 w-5" aria-hidden />
                ) : (
                  <Users className="h-5 w-5" aria-hidden />
                )}
              </span>
              <div>
                <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {preview.data.scopeName}
                </h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Convite para {preview.data.scope === "org" ? "a organização" : "o time"} como {preview.data.role}
                </p>
              </div>
            </div>

            {user?.email?.toLowerCase() !== preview.data.email.toLowerCase() ? (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                Este convite é para <strong>{preview.data.email}</strong>, mas você está logado como{" "}
                <strong>{user?.email}</strong>. Entre com a conta certa para aceitar.
              </p>
            ) : (
              <button
                type="button"
                onClick={onAccept}
                disabled={accept.isPending}
                className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
              >
                {accept.isPending ? "Aceitando…" : "Aceitar convite"}
              </button>
            )}
            {accept.isError && (
              <p className="text-sm text-red-600 dark:text-red-400">
                Não foi possível aceitar: {(accept.error as Error).message}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
